/**
 * @fileoverview PHARMAKON — Règlement d'un achat (batch-1 §3.5)
 * @module config/reglement-achat
 *
 * La couche CONFIG (L-2) du statut de règlement déclaré à la validation d'une
 * facture d'achat. Jumelle côté client de
 * `apps/server/src/config/achat-secours.config.js` (bloc §3.5) : mêmes trois
 * valeurs, mêmes libellés français, même défaut.
 *
 * Pourquoi une copie et non un import : `apps/server` et `apps/web-pharmacy`
 * sont deux artefacts de déploiement distincts (le même arbitrage que
 * `telephone-dz`), et un import inter-paquet non publié casserait l'image de
 * production. Le test `reglement-achat.config.test.js` épingle les deux côtés
 * sur la même table — une dérive fait rougir la suite.
 *
 * DÉVIATION DU PLAN, assumée : le plan §3.5 pose une colonne
 * `FactureAchat.statut_reglement` dérivée de `Σ ReglementAchat`. Ces deux
 * objets appartiennent à la vague 3 (le grand livre d'officine) et le schéma
 * n'est pas touché par cette vague. Ce qui est livré ici est la DÉCLARATION de
 * l'opérateur, écrite dans la trace d'audit de la validation et relue pour
 * afficher le badge. Quand la vague 3 posera les pièces, elles deviennent la
 * source et cette dérivation s'efface — il n'y aura jamais eu deux vérités.
 */

export const STATUT_REGLEMENT = Object.freeze({
  NON_REGLEE: "NON_REGLEE",
  PARTIELLEMENT_REGLEE: "PARTIELLEMENT_REGLEE",
  REGLEE: "REGLEE",
});

export const STATUT_REGLEMENT_VALUES = Object.freeze(
  Object.values(STATUT_REGLEMENT),
);

/** Copie normative (§8.5) — vouvoiement, jamais de valeur brute à l'écran. */
export const STATUT_REGLEMENT_LIBELLES = Object.freeze({
  NON_REGLEE: "Non réglée",
  PARTIELLEMENT_REGLEE: "Réglée partiellement",
  REGLEE: "Réglée",
});

/** Ton d'interface par statut — l'œil doit trier avant de lire. */
export const STATUT_REGLEMENT_TONS = Object.freeze({
  NON_REGLEE: "warning",
  PARTIELLEMENT_REGLEE: "info",
  REGLEE: "success",
});

/**
 * Défaut de la fenêtre. « Non réglée » est le seul défaut honnête : il porte la
 * facture au relevé en reste dû intégral, ce qui se corrige d'un règlement ;
 * l'inverse effacerait une dette d'un clic distrait.
 */
export const STATUT_REGLEMENT_DEFAUT = STATUT_REGLEMENT.NON_REGLEE;

/**
 * Les trois options de la fenêtre, dans l'ordre où elles se lisent dans la fenêtre « Valider la
 * facture » (D5, design/ux/02_achats.md §4.16) : Non réglée, Réglée, puis Réglée partiellement,
 * suivie de son montant.
 */
export const REGLEMENT_OPTIONS = Object.freeze([
  {
    value: STATUT_REGLEMENT.NON_REGLEE,
    label: STATUT_REGLEMENT_LIBELLES.NON_REGLEE,
    aide: "Aucun règlement pour l'instant — la facture apparaîtra au relevé pour son montant total.",
  },
  {
    value: STATUT_REGLEMENT.REGLEE,
    label: STATUT_REGLEMENT_LIBELLES.REGLEE,
    aide: "La facture est soldée pour son montant total.",
  },
  {
    value: STATUT_REGLEMENT.PARTIELLEMENT_REGLEE,
    label: STATUT_REGLEMENT_LIBELLES.PARTIELLEMENT_REGLEE,
    aide: "Saisissez le montant déjà versé ; le reste dû est calculé.",
  },
]);

/**
 * Un montant partiel est recevable côté client s'il est strictement compris
 * entre 0 et le total. Le serveur refait ce contrôle contre le TTC réel — le
 * client ne fait qu'éviter un aller-retour perdu.
 *
 * @param {number} montant
 * @param {number} total
 * @returns {string|null} le message d'erreur français, ou null si valide
 */
export function erreurMontantPartiel(montant, total) {
  const m = Number(montant);
  if (!Number.isFinite(m) || m <= 0)
    return "Saisissez le montant déjà réglé (supérieur à 0).";
  if (Number.isFinite(total) && m > total)
    return `Le montant réglé dépasse le total de la facture (${total.toFixed(2)} DA). Choisissez « Réglée » si elle est soldée.`;
  return null;
}
